import {
  Lead,
  GuideSection,
  P,
  UL,
  OL,
  LI,
  Term,
  GuideLink,
  Callout,
  GuideTable,
} from './guide-ui';

/**
 * Body for the "NIS2 logging requirements explained" guide.
 * Evergreen reference content; the FAQ, related links and SEO metadata live in
 * guides-data.ts (rendered by the GuidePage shell).
 *
 * Legal accuracy notes for future editors:
 * - NIS2 = Directive (EU) 2022/2555. Transposition deadline was 17 Oct 2024;
 *   national laws vary and several Member States were late. Always defer to the
 *   reader's national transposition.
 * - Detailed technical logging specs come from Commission Implementing
 *   Regulation (EU) 2024/2690, which applies to specific digital-infrastructure
 *   and digital-service entity types (DNS, TLD registries, cloud, data centres,
 *   CDNs, managed (security) service providers, marketplaces, search engines,
 *   social networks). Other sectors follow Art. 21 + national transposition.
 * - NIS2 sets NO single statutory log-retention figure. Do not invent one.
 */
export function Nis2LoggingGuideBody() {
  return (
    <>
      <Lead>
        NIS2 makes logging and monitoring a legal obligation, not a best practice.
        If your organisation falls in scope, you must be able to record security-
        relevant events, keep them long enough to investigate an incident, monitor
        them continuously, and report incidents on a strict timeline. This guide
        explains what that means in practice: what to log, how long to keep it,
        and how the monitoring and reporting duties fit together.
      </Lead>

      <Callout title="Not legal advice">
        <p>
          This is general guidance to help you scope your logging programme. NIS2
          is a directive transposed into national law, so the binding details,
          including any minimum retention period, come from your country&apos;s
          implementing legislation and your sector regulator. Verify the specifics
          against your national transposition.
        </p>
      </Callout>

      <GuideSection id="what-is-nis2" title="What is NIS2, and why does it care about logs?">
        <P>
          The <Term>NIS2 Directive (EU) 2022/2555</Term> is the EU&apos;s updated
          cybersecurity law for essential and important entities. It replaces the
          original 2016 NIS Directive, widens the sectors in scope, and raises the
          bar on cybersecurity risk management, incident reporting, and management
          accountability. The transposition deadline for Member States was{' '}
          <Term>17 October 2024</Term>; national laws implement it and several
          countries were late, so the exact wording you must follow is your local
          transposition.
        </P>
        <P>
          Logging sits at the centre of two of NIS2&apos;s core duties: the{' '}
          <Term>risk-management measures</Term> in Article 21 (you must be able to
          detect, handle and recover from incidents) and the{' '}
          <Term>reporting obligations</Term> in Article 23 (you must report
          significant incidents quickly and accurately). You cannot detect,
          investigate, or report an incident you never recorded, which is why a
          credible logging and monitoring capability is effectively mandatory.
        </P>
      </GuideSection>

      <GuideSection id="who-is-in-scope" title="Does NIS2 apply to you?">
        <P>
          NIS2 splits regulated organisations into two tiers, both of which carry
          the same security obligations but face different supervision:
        </P>
        <UL>
          <LI>
            <Term>Essential entities</Term>: larger operators in high-criticality
            sectors such as energy, transport, banking, financial market
            infrastructure, health, drinking and waste water, digital
            infrastructure, public administration, and space.
          </LI>
          <LI>
            <Term>Important entities</Term>: operators in other critical sectors
            such as postal services, waste management, chemicals, food, manufacturing,
            digital providers, and research.
          </LI>
        </UL>
        <P>
          Scope generally follows a size-and-sector test (typically medium-sized
          and larger organisations in a listed sector), with some entity types in
          scope regardless of size. If you supply services into these sectors, your
          customers&apos; NIS2 obligations frequently flow down to you through
          contracts and supply-chain security requirements.
        </P>
      </GuideSection>

      <GuideSection id="what-to-log" title="What does NIS2 require you to log?">
        <P>
          The directive states the outcome, you must be able to detect and analyse
          incidents, while the detailed technical expectations for several
          digital-sector entities are spelled out in{' '}
          <Term>Commission Implementing Regulation (EU) 2024/2690</Term>. It calls
          for logging policies that establish what is recorded, reviewed and
          retained, and continuous monitoring of that data. In practice, a
          defensible NIS2 logging baseline captures:
        </P>
        <UL>
          <LI>
            <Term>Authentication and access events</Term>: successful and failed
            logins, MFA challenges, privilege escalation, session creation.
          </LI>
          <LI>
            <Term>Administrative and privileged actions</Term>: account creation
            and changes, permission grants, use of admin or service accounts.
          </LI>
          <LI>
            <Term>Configuration and change events</Term>: changes to systems,
            security controls, firewall rules, and critical applications.
          </LI>
          <LI>
            <Term>Network and security telemetry</Term>: traffic at boundaries,
            IDS/IPS and endpoint alerts, malware and DLP events.
          </LI>
          <LI>
            <Term>Application and system events</Term>: errors, service failures,
            and audit trails from business-critical applications.
          </LI>
          <LI>
            <Term>Data access events</Term>: access to sensitive or regulated data
            stores, especially for personal data overlapping with the GDPR.
          </LI>
        </UL>
        <P>
          Logs should carry enough context, such as accurate timestamps from a synchronised
          clock source, the acting identity, source and destination, and the outcome,
          to reconstruct a sequence of events during an investigation.
        </P>
      </GuideSection>

      <GuideSection id="retention" title="How long must you keep logs under NIS2?">
        <P>
          NIS2 does <Term>not</Term> set a single statutory retention number. The
          requirement is risk-based: keep logs long enough to detect a slow-moving
          incident, investigate it, and meet your reporting and forensic needs.
          Because attackers often dwell for months before discovery, very short
          retention undermines the directive&apos;s intent.
        </P>
        <P>
          In practice, organisations and national guidance converge on a window of
          roughly <Term>6 to 18 months</Term>, often split into a hot tier for fast
          querying and a cheaper archive tier for forensic reconstruction. Some
          national transpositions and sector regulators set their own minimums, and
          overlapping regimes (GDPR, DORA, ISO 27001, PCI DSS) may pull the figure
          in different directions.
        </P>
        <Callout title="Practical rule of thumb">
          <p>
            Set a written retention policy, document the risk basis for the period
            you chose, and verify it against your national NIS2 transposition. If
            you operate across multiple Member States, apply the strictest relevant
            requirement.
          </p>
        </Callout>
      </GuideSection>

      <GuideSection id="monitoring" title="Monitoring and detection requirements">
        <P>
          Collecting logs is not enough. NIS2 expects you to <Term>use</Term> them.
          Implementing Regulation (EU) 2024/2690 describes continuous monitoring of
          events, threshold-based alerting, and a timely, qualified response. That
          means your monitoring needs to:
        </P>
        <OL>
          <LI>
            Centralise logs from across the estate so they can be searched and
            correlated, rather than sitting in silos on individual systems.
          </LI>
          <LI>
            Distinguish baseline activity from events that warrant investigation,
            through detection rules, thresholds, and anomaly detection.
          </LI>
          <LI>
            Alert a responsible team in time to act, with enough context to triage
            and escalate.
          </LI>
          <LI>
            Support correlation across sources, so a multi-step attack is seen as
            one chain rather than scattered, unrelated lines.
          </LI>
        </OL>
        <P>
          This is the functional definition of a SIEM (Security Information and
          Event Management) capability. NIS2 does not name a product category, but
          it is hard to satisfy continuous monitoring, correlation and qualified
          response at scale without one.
        </P>
      </GuideSection>

      <GuideSection id="incident-reporting" title="Incident reporting timelines">
        <P>
          Article 23 sets a multi-stage clock for significant incidents. Your logs
          are the evidence base for every stage, which is another reason they must
          be searchable quickly:
        </P>
        <GuideTable
          head={['Stage', 'Deadline', 'What you submit']}
          rows={[
            ['Early warning', 'Within 24 hours', 'Initial notification to your CSIRT or competent authority, flagging a significant incident and whether it looks malicious or cross-border.'],
            ['Incident notification', 'Within 72 hours', 'An assessment update: severity, impact, and indicators of compromise.'],
            ['Final report', 'Within 1 month', 'A detailed report: root cause, mitigation, and the full impact.'],
          ]}
        />
        <P>
          Hitting a 24-hour early-warning window depends on having already detected
          the incident and being able to pull the relevant events fast. Slow search
          or missing logs turn a reporting deadline into a compliance failure.
        </P>
      </GuideSection>

      <GuideSection id="protecting-logs" title="Securing and protecting your logs">
        <P>
          Logs are both evidence and a target. An attacker who can edit or delete
          them can hide their tracks, so NIS2&apos;s integrity and access-control
          expectations apply to the log store itself:
        </P>
        <UL>
          <LI>
            <Term>Integrity</Term>: store logs so they cannot be silently altered
            (append-only / tamper-evident storage), preserving their evidentiary value.
          </LI>
          <LI>
            <Term>Access control</Term>: restrict who can read and administer logs,
            and log that access too.
          </LI>
          <LI>
            <Term>Availability</Term>: ensure logs survive the failure or
            compromise of the systems that produced them (centralised, off-host).
          </LI>
          <LI>
            <Term>Confidentiality and the GDPR</Term>: logs often contain personal
            data; handle and, where appropriate, redact it in line with data-
            protection obligations.
          </LI>
        </UL>
      </GuideSection>

      <GuideSection id="checklist" title="A practical NIS2 logging checklist">
        <P>Use this as a starting baseline and adapt it to your sector and national law:</P>
        <UL>
          <LI>Centralised collection of authentication, admin, change, network and application logs.</LI>
          <LI>Synchronised, accurate timestamps and consistent identity/source fields.</LI>
          <LI>A written, risk-justified retention policy (commonly 6–18 months), checked against national transposition.</LI>
          <LI>Tamper-evident storage with access control and audited log-access.</LI>
          <LI>Continuous monitoring with detection rules, thresholds and anomaly detection.</LI>
          <LI>Correlation across sources to surface multi-step attacks.</LI>
          <LI>Alerting and an incident-response process that can meet the 24h / 72h / 1-month deadlines.</LI>
          <LI>Evidence and reporting workflow that maps incidents to the Article 23 stages.</LI>
          <LI>PII handling/redaction for logs containing personal data.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse supports NIS2 logging">
        <P>
          LogPulse centralises your logs on one engine, retains them for the window
          your plan provides, and monitors them in real time: search, anomaly
          detection, and a risk-based{' '}
          <GuideLink href="/siem">Security Monitoring (SIEM)</GuideLink> layer on the
          same data, so continuous monitoring and correlation do not require a
          separate, separately-priced security stack. Detections map to NIS2, DORA
          and ISO 27001 controls, and log data stays in the EU (GDPR-compliant by
          default), which matters when your obligations are themselves EU-driven.
        </P>
        <P>
          For the full picture of how this maps to the directive, see the{' '}
          <GuideLink href="/compliance/nis2">NIS2 compliance overview</GuideLink>,
          and the related{' '}
          <GuideLink href="/compliance/dora">DORA</GuideLink> obligations if you are
          a financial entity. LogPulse supports your compliance programme and
          produces evidence; it is not itself a certification.
        </P>
      </GuideSection>
    </>
  );
}
